/**
 * Kurva mana yang memerankan tiap saluran ukur sebuah tahap.
 *
 * Mesin mencari sembilan mnemonik dan hanya sembilan itu — GR, CGR, TH, K, SP,
 * RT, NPHI, RHOB, DTC — dan sampai kini keharusan bahwa sumurnya mengejanya
 * persis begitu. Kiriman sungguhan tidak mengejanya begitu. Ke-130 sumur Limau
 * di Prabumulih membawa RESD dan RESS dan tidak satu pun bernama RT, jadi tahap
 * saturasi melaporkan "the RT curve is missing" atas sumur yang resistivitas
 * dalamnya terekam lengkap, dan tidak ada satu tempat pun di layar untuk
 * mengatakan kurva mana yang dimaksud.
 *
 * Bagian ini tempat mengatakannya. Ia berdiri di dalam jendela tahapnya sendiri
 * — bukan di satu panel setelan terpisah — sebab pertanyaan "kurva mana yang
 * dibaca" hanya punya arti di sebelah metode yang membacanya: mengganti metode
 * porositas dari densitas menjadi sonik mengganti pula saluran yang perlu
 * dijawab, dan daftar yang berdiri di tempat lain tidak ikut berubah.
 *
 * Satu berkas karena enam jendela menanyakan hal yang sama, dengan alasan yang
 * sama seperti `VshTool` menampung indikatornya: daftar yang disalin adalah
 * daftar yang akan berbeda pada nama kesepuluh.
 */
import { useState } from "react";

import type { ParameterValues } from "../types";
import { Checkbox, Dropdown, ParamCaption, type Option } from "./ui";

/** Saluran ukur yang dibaca alur deterministik — cermin `WORKFLOW_CHANNELS`. */
export const CHANNEL_LABELS: Record<string, string> = {
  GR: "Gamma ray",
  CGR: "Uranium-free gamma ray",
  TH: "Thorium",
  K: "Potassium",
  SP: "Spontaneous potential",
  RT: "Deep resistivity",
  NPHI: "Neutron porosity",
  RHOB: "Bulk density",
  DTC: "Compressional slowness",
};

/** Saluran yang dibaca tiap indikator serpih — cermin `VSH_INDICATOR_CHANNELS`. */
const VSH_CHANNELS: Record<string, string[]> = {
  gr: ["GR"],
  cgr: ["CGR"],
  thorium: ["TH"],
  potassium: ["K"],
  sp: ["SP"],
  resistivity: ["RT"],
  neutron: ["NPHI"],
  density: ["RHOB"],
  sonic: ["DTC"],
  neutron_density: ["NPHI", "RHOB"],
  neutron_sonic: ["NPHI", "DTC"],
  density_sonic: ["RHOB", "DTC"],
};

/** Saluran yang dibaca tiap metode porositas total. */
const PHIT_CHANNELS: Record<string, string[]> = {
  density: ["RHOB"],
  neutron: ["NPHI"],
  sonic: ["DTC"],
  density_neutron: ["RHOB", "NPHI"],
};

/** Saluran yang dibaca tiap metode porositas efektif — cermin `PHIE_CURVES`. */
const PHIE_CHANNELS: Record<string, string[]> = {
  total_shale: [],
  density_shale: ["RHOB"],
  neutron_shale: ["NPHI"],
  density_neutron_shale: ["RHOB", "NPHI"],
  sonic_shale: ["DTC"],
};

/**
 * Saluran yang benar-benar dibaca sebuah tahap, menurut metode yang terpilih.
 *
 * Bukan daftar tetap per tahap: tahap dua membaca RHOB saja, NPHI saja, DTC
 * saja, atau RHOB dan NPHI, dan menawarkan keempatnya sekaligus adalah tiga
 * pemilih yang tidak menentukan apa pun pada hasil yang sedang dilihat.
 *
 * Keempat aturan gabungan tahap satu — minimum, maksimum, rerata, median —
 * membaca setiap indikator yang punya kurvanya, jadi kesembilan salurannya
 * memang ditawarkan.
 */
export function channelsRead(stage: number, params: ParameterValues): string[] {
  if (stage === 1) {
    const indicator = String(params.vsh_indicator ?? "gr").toLowerCase();
    return VSH_CHANNELS[indicator] ?? Object.keys(CHANNEL_LABELS);
  }
  if (stage === 2) {
    return PHIT_CHANNELS[String(params.phit_method ?? "density_neutron").toLowerCase()]
      ?? ["RHOB"];
  }
  if (stage === 3) {
    return PHIE_CHANNELS[String(params.phie_method ?? "total_shale").toLowerCase()] ?? [];
  }
  if (stage === 4) return ["RT"];
  return [];
}

/**
 * Sama atau tidaknya dua nilai parameter.
 *
 * Tiap jendela mengirim pulang hanya yang **berubah**, dan sampai kini
 * ukurannya `!==`. Itu benar selama setiap nilai parameter berupa angka, tulisan
 * atau boolean; `curve_sources` sebuah objek, dan dua objek yang isinya sama
 * tidak pernah `===`. Tanpa baris ini, membuka jendela lalu mengembalikan
 * pilihannya ke keadaan semula tetap terkirim sebagai perubahan — dan tiap
 * perubahan menerbitkan versi parameter set yang baru.
 *
 * Kuncinya diurutkan sebelum dibandingkan: yang dibandingkan adalah isinya,
 * bukan urutan penulisannya.
 */
const canonical = (value: unknown): string => {
  // Tidak ada dan kosong adalah hal yang sama di sini. Set parameter yang
  // disimpan sebelum `curve_sources` ada sama sekali tidak memuat kuncinya,
  // jadi membuka jendelanya, memilih sebuah kurva, lalu kembali ke nama
  // kanoniknya menghasilkan `{}` — dan `{}` yang dibandingkan dengan "tidak
  // ada" terbaca sebagai perubahan, yang menerbitkan versi parameter set baru
  // tanpa satu angka pun berubah.
  const v = value ?? {};
  return typeof v === "object"
    ? JSON.stringify(Object.entries(v as Record<string, string>).sort())
    : JSON.stringify(v);
};

export const sameParam = (a: unknown, b: unknown): boolean =>
  canonical(a) === canonical(b);

/** Pemetaan saluran→mnemonik sebagaimana tersimpan, selalu sebuah objek. */
export const sourcesOf = (params: ParameterValues): Record<string, string> =>
  (params.curve_sources as Record<string, string> | undefined) ?? {};

/** Kurva yang akan dibaca sebuah saluran: yang dipilih, atau nama kanoniknya. */
export const sourceFor = (params: ParameterValues, channel: string): string =>
  sourcesOf(params)[channel] || channel;

/**
 * Pemetaan baru dengan satu saluran diubah.
 *
 * Pilihan yang sama dengan nama kanoniknya **dihapus** alih-alih disimpan
 * sebagai "RT: RT". Yang tersimpan karena itu hanyalah pertukaran yang benar-
 * benar terjadi, dan sebuah set parameter yang dikembalikan ke keadaan semula
 * tidak menyisakan keterangan tentang pertukaran yang tidak berlaku lagi.
 */
export function withSource(
  params: ParameterValues,
  channel: string,
  mnemonic: string,
): Record<string, string> {
  const next = { ...sourcesOf(params) };
  if (!mnemonic || mnemonic === channel) delete next[channel];
  else next[channel] = mnemonic;
  return next;
}

/** Satu kurva sebagaimana pemilih ini perlu mengenalnya. */
export interface AvailableCurve {
  mnemonic: string;
  unit: string;
}

/**
 * Satuan yang dinormalkan: huruf besar, tanpa tanda baca.
 *
 * Satu lapangan menuliskan resistivitasnya sebagai `OHMM`, `ohm.m` dan `ohmm`
 * di dalam kiriman yang sama, densitasnya sebagai `G/C3` dan `g/cm3`, dan gamma
 * ray-nya sebagai `GAPI` dan `API`. Ketiganya satuan yang sama, dan sebuah
 * perbandingan tulisan apa adanya menyimpulkan sebaliknya pada dua pertiga
 * berkasnya.
 */
const unitKey = (unit: string): string =>
  (unit || "").toUpperCase().replace(/[^A-Z0-9%]/g, "");

/**
 * Satuan yang sah bagi tiap saluran — inilah arti "sejenis" di sini.
 *
 * Satuan dan bukan nama, sebab nama yang **dikenal** aplikasi ini sudah
 * dinormalkan saat impor: `MNEMONIC_ALIASES` mengubah ILD, LLD, RDEP dan
 * dua puluh nama lain menjadi RT sebelum kurvanya menyentuh basis data. Yang
 * masih berdiri dengan nama sendiri karena itu justru yang **tidak** dikenal
 * tabel itu — RESD, RESS, LN, SN di Prabumulih — dan bagi mereka satu-satunya
 * keterangan yang tersisa di berkasnya adalah satuannya.
 */
const CHANNEL_UNITS: Record<string, string[]> = {
  GR: ["GAPI", "API", "GR"],
  CGR: ["GAPI", "API", "GR"],
  TH: ["PPM"],
  K: ["%", "PCT", "PERCENT"],
  SP: ["MV", "V"],
  RT: ["OHMM", "OHM", "OHMSM", "OHMM2M"],
  NPHI: ["VV", "DEC", "FRAC", "PU", "%", "M3M3", "CFCF"],
  RHOB: ["GC3", "GCM3", "GCC", "KGM3", "GM3"],
  DTC: ["USF", "USFT", "USECFT", "USPF", "US", "USM"],
};

/**
 * Kurva keluaran alur ini sendiri, yang tidak pernah menjadi masukannya.
 *
 * Cermin `DERIVED_UNITS` di `petro/services.py`. Ia perlu disebut terpisah dari
 * satuannya sebab PHIE, PHIT, SW dan VSH semuanya ditulis dalam `v/v` — satuan
 * yang sama persis dengan NPHI — jadi penyaring satuan sendirian akan
 * menawarkan porositas efektif sebagai bacaan neutron. Memberi makan alur ini
 * dengan hasilnya sendiri bukan pilihan yang jarang dipakai, melainkan pilihan
 * yang tidak berarti apa-apa.
 */
const DERIVED = new Set([
  "VSH", "PHIT", "PHIE", "SW", "BVW", "PERM", "NET_RES", "NET_PAY",
  "ROCKTYPE", "ROCKTYPE_ML", "FZI", "RQI", "R35",
]);

/** Benar bila kurva ini mengukur hal yang sama dengan saluran itu. */
export function sameKind(channel: string, curve: AvailableCurve): boolean {
  if (DERIVED.has(curve.mnemonic.toUpperCase())) return false;
  const key = unitKey(curve.unit);
  // Tanpa satuan tidak ada yang bisa dinilai. Ia tidak dibuang — hanya tidak
  // bisa mengaku sejenis, dan tempatnya di daftar yang diperlebar.
  if (!key) return false;
  return (CHANNEL_UNITS[channel] ?? []).includes(key);
}

/**
 * Pilihan sebuah saluran: nama kanoniknya, kurva sejenis di sumur ini, dan —
 * bila `wide` — segala kurva lain yang tersisa.
 *
 * Yang sejenis lebih dulu dan tanpa syarat. Sisanya di belakang, ditandai
 * satuannya, dan hanya muncul ketika pembacanya memintanya: sebuah daftar yang
 * menawarkan PHIE dan SW sebagai bacaan resistivitas dalam menawarkan pilihan
 * yang tidak berarti apa-apa, dan pilihan semacam itu ditutup, bukan
 * dipajang.
 *
 * Pilihan yang **sudah tersimpan** selalu ikut dipajang meski tidak ada di
 * sumur ini, dengan alasan yang sama seperti `withChosen` pada pemilih batas
 * lingkup analisis: satu set parameter berlaku untuk seluruh lapangan, jadi
 * pilihan yang sah di 40 sumur akan bertemu sumur ke-41 yang tidak merekamnya —
 * dan pilihan yang hilang dari daftarnya terpajang sebagai "—" sementara yang
 * berangkat ke mesin tetap nama itu.
 */
export function sourceOptions(
  channel: string,
  available: AvailableCurve[],
  chosen: string,
  wide = false,
): Option<string>[] {
  const have = new Map(available.map((c) => [c.mnemonic, c]));
  const kin = available.filter((c) => c.mnemonic !== channel && sameKind(channel, c));
  const rest = available.filter(
    (c) => c.mnemonic !== channel && !sameKind(channel, c),
  );

  const rows: Option<string>[] = [
    have.has(channel)
      ? { value: channel, label: channel, hint: "as named" }
      : { value: channel, label: channel, hint: "not in this well" },
    ...kin.map((c) => ({ value: c.mnemonic, label: c.mnemonic, hint: c.unit })),
  ];
  // Diperlebar sendiri ketika tidak satu pun kurva sejenis ada: sebuah pemilih
  // yang hanya memuat nama yang tidak ada di sumur ini adalah pemilih mati, dan
  // sumur yang berkasnya tidak menuliskan satu satuan pun adalah kasusnya.
  if (wide || kin.length === 0) {
    rows.push(
      ...rest.map((c) => ({
        value: c.mnemonic,
        label: c.mnemonic,
        hint: c.unit ? `${c.unit} · other kind` : "unit not stated",
      })),
    );
  }
  if (chosen && chosen !== channel && !rows.some((r) => r.value === chosen)) {
    rows.unshift({
      value: chosen,
      label: chosen,
      hint: have.has(chosen) ? have.get(chosen)!.unit : "not in this well",
    });
  }
  return rows;
}

/**
 * Satu baris per saluran yang dibaca tahap ini.
 *
 * Tidak menggambar apa pun ketika tahapnya tidak membaca satu kurva ukur pun —
 * porositas efektif "Total − Vsh·PHIsh" adalah kasusnya, dan ia mengurangi
 * bagian serpih dari PHIT yang sudah dihitung tahap dua. Kepala bagian tanpa
 * satu baris pun di bawahnya terbaca seperti daftar yang gagal dimuat, jadi
 * yang mengatakannya adalah `ComputedInputs` di sebelahnya.
 */
export function CurveSourceFields({
  params,
  available,
  channels,
  onChange,
  disabled = false,
}: {
  params: ParameterValues;
  /** Kurva yang benar-benar ada di set kurva aktif sumur ini, berikut satuannya. */
  available: AvailableCurve[];
  /** Saluran yang dibaca tahap ini — biasanya `channelsRead(stage, params)`. */
  channels: string[];
  onChange: (sources: Record<string, string>) => void;
  disabled?: boolean;
}) {
  /**
   * Sekali untuk seluruh jendela, bukan satu per baris.
   *
   * Melebarkan daftar adalah keputusan tentang cara bekerja — "saya tahu
   * satuannya tidak tertulis benar di berkas ini" — dan bukan tentang satu
   * saluran. Tiga sakelar di tiga baris yang selalu digeser bersamaan adalah
   * dua sakelar lebih banyak daripada yang ditanyakan.
   */
  const [wide, setWide] = useState(false);
  if (channels.length === 0) return null;
  const have = new Map(available.map((c) => [c.mnemonic, c]));
  //: Kurva yang tidak sejenis dengan satu pun saluran yang ditanyakan di sini.
  const hidden = wide
    ? 0
    : available.filter(
        (curve) =>
          !channels.some((channel) => channel === curve.mnemonic || sameKind(channel, curve)),
      ).length;
  return (
    <>
      <ParamCaption>Input logs</ParamCaption>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {channels.map((channel) => {
          const chosen = sourceFor(params, channel);
          return (
            <div
              key={channel}
              style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}
            >
              <span
                style={{
                  width: 108,
                  flex: "none",
                  font: "400 10.5px var(--font-sans)",
                  color: "var(--ink-3)",
                }}
                title={CHANNEL_LABELS[channel] ?? channel}
              >
                {CHANNEL_LABELS[channel] ?? channel}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Dropdown
                  ariaLabel={`Curve read as ${channel}`}
                  value={chosen}
                  options={sourceOptions(channel, available, chosen, wide)}
                  onChange={(value) => onChange(withSource(params, channel, value))}
                  width="100%"
                  disabled={disabled}
                />
              </div>
            </div>
          );
        })}
      </div>
      {/* Yang dipilih dan tidak ada di sumur ini disebut di sini, sekali, alih-
          alih diam sampai run-nya selesai dengan lampu kuning tanpa sebab.
          Saluran yang kosong **tidak** jatuh kembali ke nama kanoniknya: hasil
          yang diam-diam dihitung dari kurva lain adalah angka yang benar dari
          pertanyaan yang tidak diajukan. */}
      {channels.some((channel) => !have.has(sourceFor(params, channel))) && (
        <span
          style={{ font: "400 9.5px var(--font-sans)", color: "var(--amber)", lineHeight: 1.5 }}
        >
          {channels
            .filter((channel) => !have.has(sourceFor(params, channel)))
            .map((channel) => `${sourceFor(params, channel)} (as ${channel})`)
            .join(", ")}
          {" is not among this well's active curves, so this stage cannot read it."}
        </span>
      )}

      {/* Yang tidak ditawarkan disebut cacahnya, bukan didiamkan. Sebuah daftar
          yang menyembunyikan enam kurva tanpa mengatakannya tidak bisa
          dibedakan dari sumur yang memang hanya punya dua — dan yang mencari
          kurvanya di situ menyimpulkan kurvanya hilang. */}
      {(hidden > 0 || wide) && (
        <Checkbox
          checked={wide}
          onChange={setWide}
          label="Offer curves of other kinds"
          meta={wide ? "all curves" : `${hidden} hidden`}
        />
      )}
      <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
        {wide
          ? "Every active curve in this well, whatever it measures. The unit beside each name is the only thing saying whether it belongs here."
          : "Curves whose unit matches what this stage measures. A well whose LAS states no units at all is offered everything, since there is then nothing to match on."}
      </span>
    </>
  );
}

/**
 * Apa yang dibaca sebuah tahap ketika yang dibacanya bukan kurva ukur.
 *
 * Permeabilitas membaca PHIE dan SW; cutoff membaca VSH, PHIE dan SW. Ketiganya
 * dihitung tahap satu sampai empat pada run yang sama, jadi tidak ada sumber
 * yang bisa dipilih di sini — dan sebuah pemilih yang bisa digambar tetapi tidak
 * mengubah satu angka pun lebih buruk daripada tidak ada pemilih. Yang berguna
 * justru kalimat yang menyebut dari tahap mana angkanya datang, sebab itulah
 * tempat menggesernya.
 */
export function ComputedInputs({ rows }: { rows: { curve: string; from: string }[] }) {
  return (
    <>
      <ParamCaption>Input logs</ParamCaption>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {rows.map((row) => (
          <div key={row.curve} style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span
              style={{
                width: 48,
                flex: "none",
                font: "500 10px var(--font-mono)",
                color: "var(--ink-2)",
              }}
            >
              {row.curve}
            </span>
            <span style={{ font: "400 10px var(--font-sans)", color: "var(--ink-5)" }}>
              {row.from}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}
